import * as React from 'react';
import * as styles from './ComposerPlaceholder.css';
import { IAIcon } from '../../icon/Icon';
import { IASpinner } from '../../spinner/Spinner';
import { LocalizationService } from '../../../services/LocalizationService';
import { ENTER_KEY, SPACEBAR_KEY } from '../../../global/Constants';

export interface IComposerPlaceholderProps {
  id: string;
  highlightColor: string;
  isActive: boolean;
  icon?: {
    name: "Picture" | "Video" | "ImageGallery2" | "Audio",
    size: number;
  };
  text?: string;
  uploadButtonText?: string;
  showSpinner?: boolean;
  progressBarText?: string;
  progressBarPercentage?: number
  enableUpload?: boolean;
  multiUpload?: boolean;
  fileType?: "image" | "video" | "audio";
  borderRadius?: number;
  uploadFileCallback?: (file: File) => Promise<void>;
  uploadMultipleFilesCallback?: (files: FileList) => Promise<void>;
  onClick: () => void;
}

export interface IComposerPlaceholderState {
  isDragging: boolean;
  showPreparingUpload: boolean;
}

export class IAComposerPlaceholder extends React.Component<IComposerPlaceholderProps, IComposerPlaceholderState> {
  private inputRef: HTMLInputElement;
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IComposerPlaceholderProps) {
    super(props);
    this.state = {
      isDragging: false,
      showPreparingUpload: false
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public render(): JSX.Element {
    const showProgressBar = this.props.progressBarPercentage !== undefined || this.state.showPreparingUpload;
    const enableUpload = this.props.enableUpload && this.props.isActive;
    let border = "";
    if (enableUpload) border = `2px dashed ${this.props.highlightColor}`;
    else if (this.props.isActive) border = `2px solid ${this.props.highlightColor}`;

    return <label
      className={[styles.IA_composerPlaceholder, "IA_OnFocus"].join(" ")}
      style={{
        backgroundColor: this.state.isDragging ? "#e8e8e8" : "#f9f9f9",
        border: border,
        cursor: enableUpload ? "pointer" : "",
        borderRadius: this.props.borderRadius ?? 0
      }}
      tabIndex={enableUpload ? 0 : -1}
      role={enableUpload ? "button" : undefined}
      onKeyDown={e => {
        if (enableUpload && (e.key === ENTER_KEY || e.key === SPACEBAR_KEY)) {
          e.preventDefault();
          this.inputRef?.click();
        }
      }}
      onDragOver={e => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0 && !this.state.isDragging && enableUpload) {
          this.setState({ isDragging: true });
        }
      }}
      onDragEnter={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragLeave={e => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.isDragging) {
          this.setState({ isDragging: false });
        }
      }}
      onDrop={e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });
        const file = e.dataTransfer.files[0];
        const acceptedType = this.props.fileType ?? "image";
        if (file?.type && file.type.indexOf(acceptedType) !== -1 && enableUpload) {
          this.setState({ showPreparingUpload: true });
          if (this.props.multiUpload) {
            this.props.uploadMultipleFilesCallback(e.dataTransfer.files).then(() => {
              this.setState({ showPreparingUpload: false });
            });
          } else {
            this.props.uploadFileCallback(e.dataTransfer.files[0]).then(() => {
              this.setState({ showPreparingUpload: false });
            });
          }
        }
      }}
      onClick={() => this.props.onClick()}
    >
      {enableUpload &&
        <input
          ref={inputRef => this.inputRef = inputRef}
          type="file"
          accept={this.props.fileType === "video" ? "video/mp4,video/x-m4v,video/*" : (this.props.fileType === "audio" ? "audio/mp3" : "image/*")}
          style={{
            display: "none"
          }}
          id={`fileInput${this.props.id}`}
          multiple={!!this.props.multiUpload}
          onChange={() => {
            const files = this.inputRef.files;
            if (files?.length > 0) {
              this.setState({ showPreparingUpload: true });
              if (this.props.multiUpload) {
                this.props.uploadMultipleFilesCallback(files).then(() => {
                  this.setState({ showPreparingUpload: false });
                });
              } else {
                this.props.uploadFileCallback(files[0]).then(() => {
                  this.setState({ showPreparingUpload: false });
                });
              }

            }
          }}
        />
      }
      {this.props.showSpinner && !showProgressBar &&
        <IASpinner color={this.props.highlightColor} />
      }
      {showProgressBar &&
        <>
          <p>{this.state.showPreparingUpload && this.props.progressBarPercentage === undefined ? this.localizationService.strings.Composer_PreparingUpload : this.props.progressBarText ?? ""}</p>
          <div className={styles.IA_composerPlaceholderProgressBarBackground}>
            <div className={styles.IA_composerPlaceholderProgressBarForeground}
              style={{
                width: this.props.progressBarPercentage === undefined || this.props.progressBarPercentage === 0 ? 0 : `${this.props.progressBarPercentage}%`,
                backgroundColor: this.props.highlightColor
              }}
            />
          </div>
        </>
      }
      {!this.props.showSpinner && !showProgressBar &&
        <div className={styles.IA_composerPlaceholderContent}>
          {this.props.icon &&
            <IAIcon
              title={this.props.icon.name}
              size={this.props.icon.size}
              color={"#777777"}
              style={{ margin: "5px 10px 0 0" }}
            />
          }
          <div className={styles.IA_composerPlaceholderText}>
            <div>{this.props.text ?? ""}</div>
            {enableUpload &&
              <div style={{ color: this.props.highlightColor }}>
                {this.props.uploadButtonText ?? ""}
              </div>
            }
          </div>
        </div>
      }
    </label>;
  }
}